<template>
  <div class="knowledge-base">
    <aside class="knowledge-sidebar">
      <div class="sidebar-header">
        <div>
          <p class="eyebrow">Metodika</p>
          <h2>Príručka hypotekára</h2>
        </div>
        <p>{{ filteredArticles.length }} výsledkov</p>
      </div>

      <label class="search-field">
        <span>Hľadať</span>
        <input
          v-model.trim="search"
          type="search"
          placeholder="napr. refinancovanie, príjem, SLSP"
        >
      </label>

      <div v-if="activeFilterChips.length" class="active-filters">
        <button
          v-for="chip in activeFilterChips"
          :key="chip.type"
          type="button"
          class="filter-chip"
          :title="`Zrušiť filter: ${chip.label}`"
          @click="removeFilter(chip.type)"
        >
          <span>{{ chip.label }}</span>
          <strong aria-hidden="true">×</strong>
        </button>
        <button type="button" class="clear-filters" @click="clearFilters">
          Zobraziť všetko
        </button>
      </div>

      <p v-if="loadError" class="fallback-note">
        Zobrazujem lokálnu kópiu metodiky. Ukladanie bude dostupné po nasadení API.
      </p>

      <div v-if="isAdmin" class="filter-group">
        <div class="filter-label">Stav</div>
        <button
          v-for="status in statuses"
          :key="status.value"
          type="button"
          :class="{ active: selectedStatus === status.value }"
          @click="selectedStatus = status.value"
        >
          {{ status.label }}
        </button>
      </div>

      <div class="filter-group">
        <div class="filter-label">Témy</div>
        <button
          v-for="topic in topics"
          :key="topic"
          type="button"
          :class="{ active: selectedTopic === topic }"
          @click="selectedTopic = topic"
        >
          {{ topic }}
        </button>
      </div>

      <div class="filter-group">
        <div class="filter-label">Banky</div>
        <button
          v-for="bank in banks"
          :key="bank"
          type="button"
          :class="{ active: selectedBank === bank }"
          @click="selectedBank = bank"
        >
          {{ bank }}
        </button>
      </div>
    </aside>

    <main class="knowledge-results">
      <div
        v-for="article in filteredArticles"
        :key="article.id"
        class="article-row"
        :class="{ active: selectedArticle && selectedArticle.id === article.id }"
      >
        <button type="button" class="article-row-main" @click="selectArticle(article)">
          <span class="row-title">{{ article.title }}</span>
          <span class="row-summary">{{ previewText(article) }}</span>
          <span class="row-tags">
            <span v-if="isAdmin && article.statusKey === 'draft'" class="tag draft">rozpracované</span>
            <span v-for="topic in article.topics.slice(0, 2)" :key="`${article.id}-${topic}`" class="tag">
              {{ topic }}
            </span>
            <span v-for="bank in article.banks.slice(0, 2)" :key="`${article.id}-${bank}`" class="tag bank">
              {{ bank }}
            </span>
          </span>
        </button>
        <button
          v-if="isAdmin"
          type="button"
          class="icon-button row-edit-button"
          title="Upraviť článok"
          aria-label="Upraviť článok"
          @click.stop="openEditor(article)"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 20h4.2L18.8 9.4l-4.2-4.2L4 15.8V20zM16 3.8l4.2 4.2 1.1-1.1a1.5 1.5 0 0 0 0-2.1l-2.1-2.1a1.5 1.5 0 0 0-2.1 0L16 3.8z"/>
          </svg>
        </button>
      </div>

      <div v-if="!loading && filteredArticles.length === 0" class="empty-state">
        <h3>Nič som nenašiel</h3>
        <p>Skúste kratší výraz alebo vypnite niektorý filter.</p>
      </div>
    </main>

    <article class="knowledge-detail" v-if="selectedArticle && !editing">
      <div class="detail-top">
        <div>
          <p class="eyebrow">{{ statusLabel(selectedArticle) }}</p>
          <h1>{{ selectedArticle.title }}</h1>
        </div>
        <div class="detail-actions">
          <a v-if="selectedArticle.sourceUrl" :href="selectedArticle.sourceUrl" target="_blank" rel="noopener noreferrer">Zdroj</a>
          <button
            v-if="isAdmin"
            type="button"
            class="icon-button"
            title="Upraviť článok"
            aria-label="Upraviť článok"
            @click="openEditor(selectedArticle)"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 20h4.2L18.8 9.4l-4.2-4.2L4 15.8V20zM16 3.8l4.2 4.2 1.1-1.1a1.5 1.5 0 0 0 0-2.1l-2.1-2.1a1.5 1.5 0 0 0-2.1 0L16 3.8z"/>
            </svg>
          </button>
        </div>
      </div>

      <div class="detail-tags">
        <span v-if="isAdmin && selectedArticle.statusKey === 'draft'" class="tag draft">rozpracované</span>
        <span v-for="topic in selectedArticle.topics" :key="topic" class="tag">{{ topic }}</span>
        <span v-for="bank in selectedArticle.banks" :key="bank" class="tag bank">{{ bank }}</span>
      </div>

      <div class="article-body">
        <template v-for="(block, index) in articleBlocks">
          <div v-if="block.type === 'table'" :key="`table-${index}`" class="article-table-wrap">
            <table class="article-table">
              <thead>
                <tr>
                  <th v-for="header in block.headers" :key="`${index}-${header}`">{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIndex) in block.rows" :key="`${index}-${rowIndex}`">
                  <td v-for="(cell, cellIndex) in row" :key="`${index}-${rowIndex}-${cellIndex}`">
                    <template v-for="(part, partIndex) in inlineParts(cell)">
                      <a
                        v-if="part.url"
                        :key="`table-link-${index}-${rowIndex}-${cellIndex}-${partIndex}`"
                        :href="part.url"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {{ part.text }}
                      </a>
                      <span v-else :key="`table-text-${index}-${rowIndex}-${cellIndex}-${partIndex}`">{{ part.text }}</span>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <ul v-else-if="block.type === 'list'" :key="`list-${index}`" class="article-list">
            <li v-for="(item, itemIndex) in block.items" :key="`${index}-${itemIndex}`">
              <template v-for="(part, partIndex) in item.parts">
                <a
                  v-if="part.url"
                  :key="`list-link-${index}-${itemIndex}-${partIndex}`"
                  :href="part.url"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ part.text }}
                </a>
                <span v-else :key="`list-text-${index}-${itemIndex}-${partIndex}`">{{ part.text }}</span>
              </template>
            </li>
          </ul>

          <component
            v-else
            :is="block.tag"
            :key="`text-${index}`"
            :class="block.className"
          >
            <template v-for="(part, partIndex) in block.parts">
              <a
                v-if="part.url"
                :key="`link-${index}-${partIndex}`"
                :href="part.url"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ part.text }}
              </a>
              <span v-else :key="`text-${index}-${partIndex}`">{{ part.text }}</span>
            </template>
          </component>
        </template>
      </div>
    </article>

    <form v-if="selectedArticle && editing" class="knowledge-detail editor-panel" @submit.prevent="saveArticle">
      <div class="detail-top">
        <div>
          <p class="eyebrow">Editor</p>
          <h1>Upraviť príspevok</h1>
        </div>
        <div class="detail-actions">
          <button type="button" class="secondary-button" @click="closeEditor">Zrušiť</button>
          <button type="submit" class="primary-button" :disabled="saving">
            {{ saving ? "Ukladám..." : "Uložiť" }}
          </button>
        </div>
      </div>

      <label class="editor-field">
        <span>Názov</span>
        <input v-model.trim="editForm.title" type="text" required>
      </label>

      <label class="editor-field">
        <span>Krátke zhrnutie</span>
        <textarea v-model.trim="editForm.summary" rows="3"></textarea>
      </label>

      <div class="editor-row">
        <label class="editor-field">
          <span>Stav</span>
          <select v-model="editForm.status">
            <option value="published">Publikované</option>
            <option value="draft">Rozpracované</option>
          </select>
        </label>

        <label class="editor-field">
          <span>Zdrojová URL</span>
          <input v-model.trim="editForm.sourceUrl" type="url">
        </label>
      </div>

      <div class="editor-row">
        <label class="editor-field">
          <span>Témy</span>
          <input v-model.trim="editForm.topics" type="text" placeholder="Príjem, Refinancovanie">
        </label>

        <label class="editor-field">
          <span>Banky</span>
          <input v-model.trim="editForm.banks" type="text" placeholder="SLSP, VÚB">
        </label>
      </div>

      <label class="editor-field editor-body">
        <span>Obsah</span>
        <div class="editor-toolbar" aria-label="Formátovanie obsahu">
          <button type="button" @click="insertHeading(2)">H2</button>
          <button type="button" @click="insertHeading(3)">H3</button>
          <button type="button" @click="insertList">Odrážky</button>
          <button type="button" @click="insertBankTable">Tabuľka bánk</button>
        </div>
        <textarea ref="bodyTextarea" v-model="editForm.body" rows="18" required></textarea>
      </label>
    </form>
  </div>
</template>

<script>
import {
  getKnowledgeBaseArticles,
  patchKnowledgeBaseArticle,
  postKnowledgeBaseArticle,
} from "@/services/brokerService";
import { knowledgeBaseArticles } from "@/data/knowledgeBase";

export default {
  name: "KnowledgeBasePanel",
  props: {
    initialBank: {
      type: String,
      default: null,
    },
    initialTopic: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    articles: [],
    search: "",
    selectedArticleId: null,
    selectedBank: "Všetky",
    selectedTopic: "Všetky",
    selectedStatus: "all",
    editing: false,
    editForm: null,
    loading: false,
    saving: false,
    loadError: false,
  }),
  computed: {
    isAdmin () {
      return this.$store.getters["userState/getRole"] === "alex_admin";
    },
    visibleArticles () {
      if (this.isAdmin) return this.articles;
      return this.articles.filter((article) => article.statusKey === "published");
    },
    banks () {
      return ["Všetky", ...uniqueSorted(this.visibleArticles.flatMap((article) => article.banks))];
    },
    topics () {
      return ["Všetky", ...uniqueSorted(this.visibleArticles.flatMap((article) => article.topics))];
    },
    statuses () {
      return [
        { value: "all", label: "Všetky" },
        { value: "published", label: "Publikované" },
        { value: "draft", label: "Rozpracované" },
      ];
    },
    activeFilterChips () {
      const chips = [];
      if (this.search) chips.push({ type: "search", label: `Hľadanie: ${this.search}` });
      if (this.isAdmin && this.selectedStatus !== "all") {
        chips.push({ type: "status", label: `Stav: ${this.statusDisplay(this.selectedStatus)}` });
      }
      if (this.selectedTopic !== "Všetky") chips.push({ type: "topic", label: `Téma: ${this.selectedTopic}` });
      if (this.selectedBank !== "Všetky") chips.push({ type: "bank", label: `Banka: ${this.selectedBank}` });
      return chips;
    },
    filteredArticles () {
      const query = normalize(this.search);

      return this.visibleArticles.filter((article) => {
        if (this.isAdmin && this.selectedStatus !== "all" && article.statusKey !== this.selectedStatus) return false;
        if (this.selectedTopic !== "Všetky" && !article.topics.includes(this.selectedTopic)) return false;
        if (this.selectedBank !== "Všetky" && !article.banks.includes(this.selectedBank)) return false;
        if (!query) return true;

        const haystack = normalize([
          article.title,
          article.summary,
          statusLabel(article),
          article.banks.join(" "),
          article.topics.join(" "),
          article.body,
        ].join(" "));

        return haystack.includes(query);
      });
    },
    selectedArticle () {
      if (this.selectedArticleId) {
        const selected = this.articles.find((article) => article.id === this.selectedArticleId);
        if (selected && this.filteredArticles.some((article) => article.id === selected.id)) return selected;
      }

      return this.filteredArticles[0] || null;
    },
    articleParagraphs () {
      if (!this.selectedArticle) return [];
      const title = normalize(this.selectedArticle.title);
      let skippedTitle = false;

      return this.selectedArticle.body
        .split("\n")
        .map((paragraph) => paragraph.trim())
        .filter((paragraph) => {
          if (!paragraph || paragraph === "‍") return false;
          if (!skippedTitle && normalize(paragraph) === title) {
            skippedTitle = true;
            return false;
          }
          return true;
        });
    },
    articleBlocks () {
      return parseArticleBlocks(this.articleParagraphs, this.selectedArticle);
    },
  },
  watch: {
    filteredArticles (articles) {
      if (!articles.length) {
        this.selectedArticleId = null;
        this.editing = false;
        return;
      }
      if (!this.selectedArticle || !articles.some((article) => article.id === this.selectedArticle.id)) {
        this.selectedArticleId = articles[0].id;
        this.editing = false;
      }
    },
  },
  async created () {
    await this.loadArticles();

    if (this.initialBank && this.banks.includes(this.initialBank)) {
      this.selectedBank = this.initialBank;
    }
    if (this.initialTopic && this.topics.includes(this.initialTopic)) {
      this.selectedTopic = this.initialTopic;
    }
    this.selectedArticleId = this.filteredArticles[0]?.id || null;
  },
  methods: {
    async loadArticles () {
      this.loading = true;
      this.loadError = false;
      try {
        const response = await getKnowledgeBaseArticles();
        const results = response.results || response;
        this.articles = (results || []).map(normalizeApiArticle);
        if (!this.articles.length) this.articles = localArticles();
      } catch (error) {
        this.loadError = true;
        this.articles = localArticles();
      } finally {
        this.loading = false;
      }
    },
    selectArticle (article) {
      this.selectedArticleId = article.id;
      this.editing = false;
    },
    previewText (article) {
      const text = article.summary || article.body || "";
      return text
        .split("\n")
        .map((paragraph) => paragraph.trim())
        .filter((paragraph) => paragraph && !isNoiseParagraph(paragraph))
        .join(" ")
        .replace(/\s+/g, " ")
        .slice(0, 155);
    },
    statusLabel,
    inlineParts,
    statusDisplay (statusValue) {
      return this.statuses.find((status) => status.value === statusValue)?.label || statusValue;
    },
    removeFilter (type) {
      if (type === "search") this.search = "";
      if (type === "status") this.selectedStatus = "all";
      if (type === "topic") this.selectedTopic = "Všetky";
      if (type === "bank") this.selectedBank = "Všetky";
    },
    clearFilters () {
      this.search = "";
      this.selectedStatus = "all";
      this.selectedTopic = "Všetky";
      this.selectedBank = "Všetky";
    },
    openEditor (article) {
      this.selectedArticleId = article.id;
      this.editing = true;
      this.editForm = {
        apiId: article.apiId,
        slug: article.slug,
        title: article.title,
        summary: article.summary,
        body: article.body,
        topics: article.topics.join(", "),
        banks: article.banks.join(", "),
        status: article.statusKey,
        sourceUrl: article.sourceUrl,
        order: article.order || 0,
      };
    },
    closeEditor () {
      this.editing = false;
      this.editForm = null;
    },
    insertHeading (level) {
      const prefix = level === 3 ? "### " : "## ";
      this.replaceBodySelection((selection) => {
        const text = selection.trim() || "Nový nadpis";
        return `${prefix}${text}`;
      });
    },
    insertList () {
      this.replaceBodySelection((selection) => {
        const lines = selection
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean);
        const items = lines.length ? lines : ["Prvý bod", "Druhý bod"];
        return items.map((item) => `- ${item.replace(/^-\s*/, "")}`).join("\n");
      });
    },
    insertBankTable () {
      const banks = ["SLSP", "VÚB", "Tatra banka", "ČSOB", "UniCredit", "Prima banka", "365.bank", "mBank"];
      const rows = banks.map((bank) => `| ${bank} |  |  |`);
      const table = [
        "## Názov tabuľky",
        "| Banka | Hodnota | Poznámka |",
        "| --- | --- | --- |",
        ...rows,
      ].join("\n");

      this.replaceBodySelection(() => table, { surroundWithBlankLines: true });
    },
    replaceBodySelection (factory, options = {}) {
      const textarea = this.$refs.bodyTextarea;
      if (!textarea || !this.editForm) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = this.editForm.body.slice(0, start);
      const selection = this.editForm.body.slice(start, end);
      const after = this.editForm.body.slice(end);
      let replacement = factory(selection);

      if (options.surroundWithBlankLines) {
        replacement = `${before.endsWith("\n\n") || !before ? "" : "\n\n"}${replacement}${after.startsWith("\n\n") || !after ? "" : "\n\n"}`;
      }

      this.editForm.body = `${before}${replacement}${after}`;
      this.$nextTick(() => {
        textarea.focus();
        textarea.setSelectionRange(start, start + replacement.length);
      });
    },
    async saveArticle () {
      if (!this.editForm) return;
      this.saving = true;

      const payload = {
        slug: this.editForm.slug || slugify(this.editForm.title),
        title: this.editForm.title,
        summary: this.editForm.summary || "",
        body: this.editForm.body || "",
        topics: splitList(this.editForm.topics),
        banks: splitList(this.editForm.banks),
        status: this.editForm.status,
        source_url: this.editForm.sourceUrl || "",
        order: this.editForm.order || 0,
      };

      try {
        const saved = this.editForm.apiId
          ? await patchKnowledgeBaseArticle(this.editForm.apiId, payload)
          : await postKnowledgeBaseArticle(payload);
        const article = normalizeApiArticle(saved);
        const index = this.articles.findIndex((item) => item.id === this.selectedArticleId || item.slug === article.slug);

        if (index >= 0) {
          this.$set(this.articles, index, article);
        } else {
          this.articles.push(article);
        }

        this.selectedArticleId = article.id;
        this.closeEditor();
        this.$store.commit("appState/setSuccessMessage", {
          text: "Metodika bola uložená.",
          type: "success",
        });
      } catch (error) {
        this.$store.commit("appState/setSuccessMessage", {
          text: "Metodiku sa nepodarilo uložiť. Skontrolujte, či je nasadené API s migráciou.",
          type: "error",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};

function localArticles () {
  return knowledgeBaseArticles.map((article, index) => ({
    apiId: null,
    id: article.id,
    slug: article.id,
    title: article.title,
    summary: article.summary || "",
    body: article.body || "",
    topics: article.topics || [],
    banks: article.banks || [],
    statusKey: article.status === "Rozpracované" ? "draft" : "published",
    sourceUrl: article.sourceUrl || "",
    order: index,
  }));
}

function normalizeApiArticle (article) {
  return {
    apiId: article.id,
    id: `api-${article.id}`,
    slug: article.slug,
    title: article.title,
    summary: article.summary || "",
    body: article.body || "",
    topics: article.topics || [],
    banks: article.banks || [],
    statusKey: article.status || "published",
    sourceUrl: article.source_url || "",
    order: article.order || 0,
  };
}

function statusLabel (article) {
  return article.statusKey === "draft" ? "Rozpracované" : "Publikované";
}

const DEFAULT_BANK_ORDER = ["SLSP", "VÚB", "Tatra banka", "ČSOB", "UniCredit", "Prima banka", "365.bank", "mBank"];

function parseArticleBlocks (lines, article) {
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (isNoiseParagraph(line)) {
      index += 1;
      continue;
    }

    const markdownTable = parseMarkdownTable(lines, index);
    if (markdownTable) {
      blocks.push(markdownTable.block);
      index = markdownTable.nextIndex;
      continue;
    }

    const legacyTable = parseLegacyTable(lines, index, article);
    if (legacyTable) {
      blocks.push(legacyTable.block);
      index = legacyTable.nextIndex;
      continue;
    }

    if (normalize(line) === "title") {
      index += 1;
      continue;
    }

    const list = parseList(lines, index);
    if (list) {
      blocks.push(list.block);
      index = list.nextIndex;
      continue;
    }

    const explicitHeading = parseExplicitHeading(line);
    const isHeadingBlock = explicitHeading || isHeading(line, lines[index + 1]);
    blocks.push({
      type: "text",
      tag: explicitHeading?.tag || (isHeadingBlock ? "h2" : "p"),
      className: explicitHeading?.className || (isHeadingBlock ? "article-heading" : "article-paragraph"),
      parts: inlineParts(explicitHeading?.text || line),
    });
    index += 1;
  }

  return blocks;
}

function parseMarkdownTable (lines, startIndex) {
  if (!isMarkdownTableLine(lines[startIndex])) return null;

  const tableLines = [];
  let index = startIndex;
  while (isMarkdownTableLine(lines[index])) {
    tableLines.push(lines[index]);
    index += 1;
  }

  if (tableLines.length < 2) return null;

  const headers = splitTableLine(tableLines[0]);
  const rowStart = isMarkdownSeparatorLine(tableLines[1]) ? 2 : 1;
  const rows = tableLines
    .slice(rowStart)
    .map(splitTableLine)
    .filter((row) => row.some(Boolean));

  if (!headers.length || !rows.length) return null;

  return {
    nextIndex: index,
    block: {
      type: "table",
      headers,
      rows: rows.map((row) => normalizeRowLength(row, headers.length)),
    },
  };
}

function parseLegacyTable (lines, startIndex, article) {
  if (normalize(lines[startIndex]) !== "title") return null;

  const previousLine = previousMeaningfulLine(lines, startIndex);
  const headers = [];
  let index = startIndex + 1;

  while (index < lines.length && headers.length < 5 && isLikelyTableHeader(lines[index])) {
    headers.push(lines[index]);
    index += 1;
  }

  if (!headers.length) return null;

  const sourceLooksLikeTable = previousLine && normalize(previousLine).includes("tabulka");
  const articleHasMultipleBanks = (article.banks || []).filter((bank) => bank !== "Všetky banky").length > 1;
  if (!sourceLooksLikeTable && !articleHasMultipleBanks) return null;

  const values = [];
  while (index < lines.length) {
    const line = lines[index];
    if (normalize(line) === "title" || isMarkdownTableLine(line)) break;
    if (values.length >= headers.length && isLikelyTableEnd(line, lines[index + 1])) break;
    if (!isNoiseParagraph(line)) values.push(line);
    index += 1;
  }

  if (!values.length) return null;

  const rows = chunkValues(values, headers.length);
  const rowBanks = inferRowBanks(article, rows.length);
  const shouldAddBankColumn = rowBanks.length === rows.length && rows.length > 1;

  return {
    nextIndex: index,
    block: {
      type: "table",
      headers: shouldAddBankColumn ? ["Banka", ...headers] : headers,
      rows: rows.map((row, rowIndex) => {
        const normalized = normalizeRowLength(row, headers.length);
        return shouldAddBankColumn ? [rowBanks[rowIndex], ...normalized] : normalized;
      }),
    },
  };
}

function parseList (lines, startIndex) {
  if (!isListLine(lines[startIndex])) return null;

  const items = [];
  let index = startIndex;
  while (isListLine(lines[index])) {
    items.push({
      parts: inlineParts(lines[index].replace(/^[-*]\s+/, "")),
    });
    index += 1;
  }

  return {
    nextIndex: index,
    block: { type: "list", items },
  };
}

function parseExplicitHeading (line) {
  const match = line.match(/^(#{2,3})\s+(.+)$/);
  if (!match) return null;

  return {
    tag: match[1].length === 3 ? "h3" : "h2",
    className: match[1].length === 3 ? "article-subheading" : "article-heading",
    text: match[2],
  };
}

function splitTableLine (line) {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

function normalizeRowLength (row, length) {
  const normalized = [...row];
  while (normalized.length < length) normalized.push("");
  return normalized.slice(0, length);
}

function chunkValues (values, chunkSize) {
  const rows = [];
  for (let index = 0; index < values.length; index += chunkSize) {
    rows.push(values.slice(index, index + chunkSize));
  }
  return rows;
}

function inferRowBanks (article, rowCount) {
  const articleBanks = (article.banks || []).filter((bank) => bank && bank !== "Všetky banky");
  if (articleBanks.length >= rowCount) return articleBanks.slice(0, rowCount);
  return DEFAULT_BANK_ORDER.slice(0, rowCount);
}

function previousMeaningfulLine (lines, startIndex) {
  for (let index = startIndex - 1; index >= 0; index -= 1) {
    if (!isNoiseParagraph(lines[index])) return lines[index];
  }
  return "";
}

function isMarkdownTableLine (line) {
  return typeof line === "string" && /^\s*\|.+\|\s*$/.test(line);
}

function isMarkdownSeparatorLine (line) {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line || "");
}

function isListLine (line) {
  return /^[-*]\s+\S/.test(line || "");
}

function isLikelyTableHeader (line) {
  if (!line || isNoiseParagraph(line)) return false;
  const normalized = normalize(line);
  if (["poznamka", "poznámka", "strop", "podiel", "trvanie", "akceptuje"].includes(normalized)) return true;
  if (/^(max|min|ltv|dti|dsti|suma|lehota|poplatok)/i.test(line)) return true;
  if (/^[0-9?%-]/.test(line.trim())) return false;
  if (/^(ano|áno|nie|neakceptuje)/i.test(normalized)) return false;
  if (line.length > 46) return false;
  return true;
}

function isLikelyTableEnd (line, nextLine) {
  if (!line || isNoiseParagraph(line)) return true;
  if (isMarkdownTableLine(line) || normalize(line) === "title") return true;
  if (isHeading(line, nextLine) && !isLikelyTableValue(line)) return true;
  if (/^[A-ZÁČĎÉÍĽĹŇÓÔŔŠŤÚÝŽ]/.test(line) && /[.!?]$/.test(line)) return true;
  return false;
}

function isLikelyTableValue (line) {
  const normalized = normalize(line);
  if (/^(ano|áno|nie|neakceptuje|nedá sa|neda sa|[-?]+)$/.test(normalized)) return true;
  if (/[0-9%€]/.test(line)) return true;
  if (/^(max|minimalne|minimálne|ako|iba|len|v pripade|pri|od|do|po|bez)/i.test(normalized)) return true;
  return line.length > 46 && /^[a-záčďéíľĺňóôŕšťúýž]/i.test(line);
}

function normalize (value) {
  return (value || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function isNoiseParagraph (paragraph) {
  return [
    "table of contents",
    "tu",
    "‍",
  ].includes(normalize(paragraph));
}

function isHeading (paragraph, nextParagraph) {
  if (!nextParagraph) return false;
  if (paragraph.length > 88) return false;
  if (/https?:\/\//i.test(paragraph)) return false;
  if (/[.!?]$/.test(paragraph)) return false;
  if (/^\(.+\)$/.test(paragraph)) return false;
  return true;
}

function inlineParts (text) {
  const urlRegex = /(https?:\/\/[^\s)]+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: text.slice(lastIndex, match.index) });
    }
    parts.push({ text: match[0], url: match[0] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ text: text.slice(lastIndex) });
  }

  return parts.length ? parts : [{ text }];
}

function uniqueSorted (values) {
  return [...new Set((values || []).filter(Boolean))].sort((a, b) => a.localeCompare(b, "sk"));
}

function splitList (value) {
  return (value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function slugify (value) {
  return normalize(value)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
</script>

<style scoped lang="scss">
.knowledge-base {
  --kb-primary: var(--app-primary);
  --kb-primary-dark: var(--app-text);
  --kb-border: var(--app-border);
  --kb-muted: var(--app-text-muted);
  --kb-soft: var(--app-surface-muted);

  display: grid;
  grid-template-columns: 300px minmax(320px, 390px) minmax(440px, 1fr);
  height: 100%;
  overflow: hidden;
  background: var(--app-bg);
  color: var(--kb-primary-dark);
  font-size: 14px;
}

.knowledge-sidebar,
.knowledge-results,
.knowledge-detail {
  min-height: 0;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track-piece {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border: 0;
    border-radius: 999px;
    outline: 0;
    background: #c9d9ef;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #adc5e6;
  }
}

.knowledge-sidebar {
  padding: 28px 24px 32px;
  border-right: 1px solid var(--kb-border);
  background: var(--app-surface-muted);
}

.sidebar-header {
  margin-bottom: 22px;

  h2 {
    margin: 5px 0 6px;
    font-size: 22px;
    font-weight: 800;
    line-height: 1.2;
  }

  p:last-child {
    margin: 0;
    color: var(--kb-muted);
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
  }
}

.eyebrow {
  margin: 0;
  color: var(--kb-primary);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.fallback-note {
  margin: 0 0 18px;
  padding: 10px 12px;
  border: 1px solid var(--app-border);
  border-radius: 8px;
  color: var(--app-text-muted);
  background: var(--app-surface);
  font-size: 12px;
  line-height: 1.5;
}

.search-field {
  display: block;
  margin-bottom: 24px;

  span {
    display: block;
    margin-bottom: 8px;
    color: var(--kb-muted);
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
  }

  input {
    width: 100%;
    height: 46px;
    padding: 0 15px;
    border: 1px solid var(--app-border);
    border-radius: 8px;
    outline: none;
    color: var(--kb-primary-dark);
    font-size: 13px;
    font-weight: 500;
    background: var(--app-surface);
    transition: border-color 160ms ease, box-shadow 160ms ease;

    &:focus {
      border-color: var(--kb-primary);
      box-shadow: 0 0 0 3px rgba(45, 125, 210, 0.12);
    }
  }
}

.active-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: -8px 0 22px;
}

.filter-chip,
.clear-filters {
  min-height: 31px;
  border-radius: 999px;
  border: 1px solid var(--app-border);
  background: var(--app-surface-muted);
  color: var(--app-primary);
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 100%;
  padding: 5px 9px 5px 12px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    color: #fff;
    background: var(--kb-primary);
    font-size: 14px;
    line-height: 1;
  }

  &:hover {
    border-color: var(--kb-primary);
    background: var(--app-primary-soft);
  }
}

.clear-filters {
  padding: 5px 12px;
  color: var(--app-text-muted);
  background: transparent;

  &:hover {
    color: var(--kb-primary-dark);
    background: var(--app-primary-soft);
  }
}

.filter-group {
  margin-bottom: 24px;

  button {
    display: block;
    width: 100%;
    min-height: 34px;
    margin-bottom: 6px;
    padding: 8px 12px;
    border: 1px solid transparent;
    border-radius: 8px;
    color: var(--app-text-muted);
    background: transparent;
    text-align: left;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 160ms ease, color 160ms ease, border-color 160ms ease;

    &:hover {
      color: var(--kb-primary-dark);
      background: var(--app-primary-soft);
    }
  }

  button.active {
    border-color: var(--app-primary);
    color: var(--app-primary);
    background: var(--app-surface-strong);
    font-weight: 800;
    box-shadow: 0 8px 18px rgba(34, 89, 164, 0.08);
  }
}

.filter-label {
  margin-bottom: 9px;
  color: var(--app-text-muted);
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.knowledge-results {
  padding: 22px 18px;
  border-right: 1px solid var(--kb-border);
  background: var(--app-bg-neutral);
}

.article-row {
  position: relative;
  margin-bottom: 12px;
  border: 1px solid var(--app-border);
  border-radius: 8px;
  background: var(--app-surface);
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;

  &:hover {
    border-color: var(--app-primary);
    box-shadow: 0 10px 22px rgba(18, 48, 96, 0.06);
    transform: translateY(-1px);
  }
}

.article-row.active {
  border-color: var(--kb-primary);
  box-shadow: 0 14px 30px rgba(36, 100, 185, 0.12);
}

.article-row-main {
  display: block;
  width: 100%;
  padding: 18px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.row-edit-button {
  position: absolute;
  top: 10px;
  right: 10px;
}

.row-title {
  display: block;
  padding-right: 34px;
  color: var(--kb-primary-dark);
  font-size: 16px;
  font-weight: 800;
  line-height: 1.28;
}

.row-summary {
  display: block;
  margin-top: 8px;
  color: var(--kb-muted);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
}

.row-tags,
.detail-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.tag {
  display: inline-flex;
  align-items: center;
  min-height: 23px;
  padding: 4px 9px;
  border-radius: 6px;
  color: var(--app-primary);
  background: var(--app-primary-soft);
  font-size: 12px;
  font-weight: 800;
}

.tag.bank {
  color: var(--app-primary-hover);
  background: var(--app-surface-strong);
}

.tag.draft {
  color: #9b3412;
  background: #fff0d9;
}

.knowledge-detail {
  padding: 44px 58px;
  background: var(--app-surface);
}

.detail-top {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-start;

  h1 {
    max-width: 780px;
    margin: 6px 0 0;
    color: var(--kb-primary-dark);
    font-size: 31px;
    font-weight: 800;
    line-height: 1.18;
  }
}

.detail-actions {
  display: flex;
  align-items: center;
  gap: 8px;

  a {
    padding: 10px 15px;
    border: 1px solid var(--app-border);
    border-radius: 8px;
    color: var(--app-primary);
    font-size: 13px;
    font-weight: 800;
    text-decoration: none;
    white-space: nowrap;
    background: var(--app-surface-muted);

    &:hover {
      background: var(--kb-soft);
    }
  }
}

.icon-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--app-border);
  border-radius: 8px;
  color: var(--app-primary);
  background: var(--app-surface-muted);
  cursor: pointer;

  svg {
    width: 17px;
    height: 17px;
    fill: currentColor;
  }

  &:hover {
    background: var(--kb-soft);
  }
}

.article-body {
  max-width: 820px;
  margin-top: 30px;
}

.article-paragraph,
.article-heading,
.article-subheading,
.article-table,
.article-list {
  a {
    color: #1f64bd;
    font-weight: 800;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
    word-break: break-word;
  }
}

.article-paragraph {
  margin: 0 0 14px;
  color: var(--app-text-muted);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.72;
}

.article-heading {
  margin: 26px 0 10px;
  color: var(--kb-primary-dark);
  font-size: 18px;
  font-weight: 800;
  line-height: 1.38;

  &:first-child {
    margin-top: 0;
  }
}

.article-subheading {
  margin: 22px 0 8px;
  color: var(--app-text);
  font-size: 16px;
  font-weight: 800;
  line-height: 1.4;
}

.article-list {
  margin: 0 0 18px;
  padding-left: 20px;
  color: var(--app-text-muted);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.7;

  li + li {
    margin-top: 6px;
  }
}

.article-table-wrap {
  max-width: 100%;
  margin: 18px 0 26px;
  overflow-x: auto;
  border: 1px solid var(--app-border);
  border-radius: 8px;
  background: var(--app-surface);
  box-shadow: 0 10px 24px rgba(21, 65, 120, 0.05);
}

.article-table {
  width: 100%;
  min-width: 620px;
  border-collapse: collapse;
  color: var(--app-text-muted);
  font-size: 13px;
  line-height: 1.45;

  th,
  td {
    padding: 13px 14px;
    border-bottom: 1px solid var(--app-border);
    text-align: left;
    vertical-align: top;
  }

  th {
    color: var(--app-text);
    background: var(--app-surface-strong);
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  tbody tr:nth-child(even) td {
    background: var(--app-surface-muted);
  }

  td:first-child,
  th:first-child {
    width: 150px;
    color: var(--kb-primary-dark);
    font-weight: 800;
  }
}

.empty-state {
  padding: 32px;
  border: 1px dashed var(--app-border);
  border-radius: 8px;
  color: var(--kb-muted);
  background: var(--app-surface);
}

.editor-panel {
  background: var(--app-bg-neutral);
}

.editor-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.editor-field {
  display: block;
  margin-top: 18px;

  span {
    display: block;
    margin-bottom: 8px;
    color: var(--app-text-muted);
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
  }

  input,
  textarea,
  select {
    width: 100%;
    border: 1px solid var(--app-border);
    border-radius: 8px;
    outline: none;
    color: var(--kb-primary-dark);
    background: var(--app-surface);
    font-family: Poppins, sans-serif;
    font-size: 13px;
    font-weight: 500;
  }

  input,
  select {
    height: 44px;
    padding: 0 13px;
  }

  textarea {
    min-height: 96px;
    padding: 12px 13px;
    line-height: 1.55;
    resize: vertical;
  }
}

.editor-body textarea {
  min-height: 320px;
}

.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;

  button {
    min-height: 34px;
    padding: 0 12px;
    border: 1px solid var(--app-border);
    border-radius: 8px;
    color: var(--app-primary);
    background: var(--app-surface-muted);
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;

    &:hover {
      background: var(--kb-soft);
    }
  }
}

.primary-button,
.secondary-button {
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  font-weight: 800;
  cursor: pointer;
}

.primary-button {
  border: 1px solid var(--kb-primary);
  color: #fff;
  background: var(--kb-primary);

  &:disabled {
    opacity: 0.65;
    cursor: default;
  }
}

.secondary-button {
  border: 1px solid var(--app-border);
  color: var(--app-primary);
  background: var(--app-surface-muted);
}

@media screen and (max-width: 1100px) {
  .knowledge-base {
    grid-template-columns: 250px minmax(280px, 1fr);
  }

  .knowledge-detail {
    grid-column: 1 / -1;
    padding: 28px;
  }
}

@media screen and (max-width: 920px) {
  .knowledge-base {
    grid-template-columns: 220px minmax(260px, 1fr);
  }

  .knowledge-sidebar {
    padding: 22px 18px;
  }

  .knowledge-results {
    padding: 18px 14px;
  }
}

@media screen and (max-width: 767px) {
  .knowledge-base {
    display: block;
    height: 100%;
    overflow: auto;
  }

  .knowledge-sidebar,
  .knowledge-results,
  .knowledge-detail {
    max-height: none;
    overflow: visible;
  }

  .knowledge-detail {
    padding: 24px 18px;
  }

  .detail-top,
  .editor-row {
    display: block;
  }

  .detail-top h1 {
    font-size: 26px;
  }

  .detail-actions {
    margin-top: 16px;
  }
}
</style>
